type DomPurifyLike = {
    sanitize: (dirty: string, options?: Record<string, unknown>) => string;
};
type DomPurifyFragmento = {
    sanitize: (dirty: string, options: Record<string, unknown>) => DocumentFragment | null;
};

let cachedPurifier: DomPurifyLike | null | undefined;

function getPurifier(): DomPurifyLike | null {
    if (cachedPurifier !== undefined) return cachedPurifier;
    try {
        // Lazy-load to avoid hard SSR crashes when the runtime cannot initialize
        // the isomorphic adapter (it caused /blog/[slug] to render 500).
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const mod = require('isomorphic-dompurify') as { default?: DomPurifyLike } & DomPurifyLike;
        cachedPurifier = mod.default ?? mod;
    } catch {
        cachedPurifier = null;
    }
    return cachedPurifier;
}

function basicHtmlFallbackSanitizer(dirty: string): string {
    return dirty
        .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
        .replace(/\son\w+="[^"]*"/gi, '')
        .replace(/\son\w+='[^']*'/gi, '')
        .replace(/\s(href|src)\s*=\s*(['"])\s*javascript:[\s\S]*?\2/gi, ' $1="#"');
}

/**
 * HTML sanitization helpers for use before `dangerouslySetInnerHTML`.
 *
 * Every callsite that renders user-authored or AI-generated HTML in
 * the app MUST pass the string through one of these functions. The
 * default DOMPurify config drops <script>, inline event handlers
 * (onclick, onerror, …), javascript: URLs, and <iframe>/<object>/<embed>,
 * which neutralizes the three XSS vectors the audit flagged:
 *
 *   - manuscriptHtml          (OCR / Claude transcription output)
 *   - Tiptap-authored landing (admin CMS)
 *   - blog article contentHtml
 *
 * Works server- and client-side via isomorphic-dompurify.
 */

/**
 * For rich, user-authored content: headings, lists, tables, images,
 * links. Anything the Tiptap editor can produce. Strips scripts and
 * dangerous attributes while keeping formatting and media.
 */
export function sanitizeRichHtml(dirty: string): string {
    if (!dirty) return '';
    const purifier = getPurifier();
    if (!purifier) return basicHtmlFallbackSanitizer(dirty);
    try {
        return purifier.sanitize(dirty, {
            USE_PROFILES: { html: true },
            ADD_ATTR: ['target', 'rel'],
            FORBID_TAGS: ['style', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
            FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onchange', 'onsubmit'],
            ALLOW_DATA_ATTR: false,
        });
    } catch {
        return basicHtmlFallbackSanitizer(dirty);
    }
}

/**
 * Texto corto que solo lleva negrita, cursiva, subrayado y saltos (los
 * bloques de texto del encabezado y del pie de un formato): cualquier otra
 * etiqueta se cae (su texto se queda) y no sobrevive ningún atributo. Sin
 * DOMPurify deja esas cuatro etiquetas, desnudas, y escapa lo demás.
 */
export function sanitizeInlineMarks(dirty: string): string {
    if (!dirty) return '';
    const purifier = getPurifier();
    if (purifier) {
        try {
            return purifier.sanitize(dirty, { ALLOWED_TAGS: ['strong', 'em', 'u', 'br'], ALLOWED_ATTR: [], KEEP_CONTENT: true });
        } catch {
            /* sigue con el respaldo */
        }
    }
    return dirty
        .replace(/<(script|style)[\s\S]*?<\/\1\s*>/gi, '')
        .replace(/<(\/?)(strong|em|u|br)\b[^>]*>|<[^>]*>?/gi, (_m, barra: string | undefined, tag: string | undefined) => (tag ? `<${barra ?? ''}${tag.toLowerCase()}>` : ''))
        .replace(/<(?!\/?(?:strong|em|u|br)>)/g, '&lt;');
}

/** ¿Hay DOMPurify? Sin él no se puede normalizar el encabezado o el pie con formato. */
export function hayPurificador(): boolean {
    return !!getPurifier();
}

/**
 * El HTML como árbol DOM ya purgado (`RETURN_DOM_FRAGMENT`), o null si no
 * hay DOMPurify o no pudo parsearlo. El fragmento es del documento inerte
 * del DOMParser de DOMPurify: no carga imágenes ni ejecuta nada.
 */
export function purificarAFragmento(dirty: string, config: Record<string, unknown>): DocumentFragment | null {
    const purifier = getPurifier() as unknown as DomPurifyFragmento | null;
    if (!purifier) return null;
    try {
        return purifier.sanitize(dirty, { ...config, RETURN_DOM_FRAGMENT: true }) ?? null;
    } catch {
        return null;
    }
}

const ETIQUETAS_BANDA_SALIDA = ['div', 'p', 'table', 'tbody', 'tr', 'td', 'strong', 'em', 'u', 'br', 'img', 'span'];
const ATRIBUTOS_BANDA_SALIDA = ['style', 'src', 'alt', 'title', 'data-ez-img', 'data-ez-pagenum', 'data-ez-ancho', 'data-ez-numpages', 'data-imagen-perfil'];
// Nada de esto sale nunca del pintor: si aparece en un estilo, no viene de él.
const ESTILO_PELIGROSO = /url\s*\(|expression\s*\(|@import|javascript:|behavior\s*:/i;

/**
 * Última barrera antes de pintar la banda con formato en la vista previa
 * (`dangerouslySetInnerHTML`): solo las etiquetas y atributos que produce el
 * pintor, y `src` solo si es una imagen propia (`esPropia`). DOMPurify deja
 * pasar `data:` en `img` aunque `ALLOWED_URI_REGEXP` no lo admita, así que
 * el `src` se vuelve a mirar sobre el árbol. Null si no hay DOMPurify: quien
 * llama pinta el texto de respaldo.
 */
export function sanitizeBandaRicaSalida(html: string, esPropia: (url: string) => boolean, uriPropia?: RegExp): string | null {
    if (!html) return '';
    const frag = purificarAFragmento(html, {
        ALLOWED_TAGS: ETIQUETAS_BANDA_SALIDA,
        ALLOWED_ATTR: ATRIBUTOS_BANDA_SALIDA,
        ALLOW_DATA_ATTR: false,
        KEEP_CONTENT: true,
        // Con ALLOWED_URI_REGEXP, DOMPurify mira con él TODO atributo que no sea
        // «seguro»: los data-ez-* (un «1», un «logo») se perderían.
        ADD_URI_SAFE_ATTR: ['data-ez-img', 'data-ez-pagenum', 'data-ez-ancho', 'data-ez-numpages', 'data-imagen-perfil'],
        ...(uriPropia ? { ALLOWED_URI_REGEXP: uriPropia } : {}),
    });
    if (!frag) return null;
    try {
        for (const el of Array.from(frag.querySelectorAll('[src]'))) {
            if (!esPropia(el.getAttribute('src') ?? '')) el.removeAttribute('src');
        }
        for (const el of Array.from(frag.querySelectorAll('[style]'))) {
            if (ESTILO_PELIGROSO.test(el.getAttribute('style') ?? '')) el.removeAttribute('style');
        }
        const caja = frag.ownerDocument.createElement('div');
        caja.appendChild(frag);
        return caja.innerHTML;
    } catch {
        return null;
    }
}

/**
 * For CSS strings that will be injected into a <style> tag
 * (e.g. article.contentStyle). CSS is NOT HTML so DOMPurify can't
 * help directly. This strips the known escape vectors: @import,
 * expression(), url(javascript:…), and any attempt to close the
 * style tag early.
 */
export function sanitizeInlineStyle(css: string): string {
    if (!css) return '';
    return css
        .replace(/<\/style>/gi, '')
        .replace(/@import[^;]*;?/gi, '')
        .replace(/expression\s*\(/gi, '')
        .replace(/url\s*\(\s*['"]?\s*javascript\s*:/gi, 'url(about:blank')
        .replace(/behavior\s*:/gi, '');
}
